import { DecimalPipe, DatePipe } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import {
  CatalogService,
  CreateHospitalityRateCalendarEntryPayload,
  HospitalityProperty,
  HospitalityRateCalendarEntry,
  HospitalityRatePlan,
  HospitalityRoomType
} from './catalog.service';

type DayHeader = { key: string; label: string; subLabel: string; date: Date; isWeekend: boolean };

@Component({
  standalone: true,
  selector: 'ain-hospitality-rate-calendar',
  imports: [
    DecimalPipe, DatePipe, AinPageComponent, AinStateComponent, RouterLink, FormsModule,
    ButtonModule, SelectModule, InputTextModule, InputNumberModule, TableModule, JalaliDateInputComponent
  ],
  template: `
    <ain-page [title]="t('catalog.hospitality.rateCalendar.title')" [description]="t('catalog.hospitality.rateCalendar.description')">
      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else {
        <section class="panel">
          <div class="toolbar">
            <p-select
              [options]="propertyOptions()"
              [(ngModel)]="selectedPropertyId"
              (ngModelChange)="onPropertyChange()"
              optionLabel="label"
              optionValue="value"
              [placeholder]="t('catalog.hospitality.property')"
              [filter]="true"
              appendTo="body" />
            <p-select
              [options]="ratePlanOptions()"
              [(ngModel)]="selectedRatePlanId"
              (ngModelChange)="loadCalendar()"
              optionLabel="label"
              optionValue="value"
              [placeholder]="t('catalog.hospitality.ratePlan')"
              [filter]="true"
              appendTo="body" />
            <button pButton type="button" icon="pi pi-chevron-left" severity="secondary" (click)="shiftWindow(-1)"></button>
            <button pButton type="button" [label]="t('catalog.rental.calendar.today')" severity="secondary" (click)="goToday()"></button>
            <button pButton type="button" icon="pi pi-chevron-right" severity="secondary" (click)="shiftWindow(1)"></button>
            <span class="window-label">{{ windowLabel() }}</span>
            <button pButton type="button" [label]="t('catalog.action.refresh')" severity="secondary" (click)="loadCalendar()"></button>
            <a routerLink="/hospitality/properties">{{ t('catalog.hospitality.properties.title') }}</a>
          </div>

          @if (!selectedPropertyId) {
            <ain-state type="empty" [title]="t('catalog.hospitality.rateCalendar.selectProperty')" />
          } @else if (!ratePlans().length) {
            <ain-state type="empty" [title]="t('catalog.hospitality.rateCalendar.noRatePlans')" />
          } @else if (!selectedRatePlanId) {
            <ain-state type="empty" [title]="t('catalog.hospitality.rateCalendar.selectRatePlan')" />
          } @else {
            <div class="legend">
              <span class="chip override">{{ t('catalog.hospitality.rateCalendar.legendOverride') }}</span>
              <span class="chip base">{{ t('catalog.hospitality.rateCalendar.legendBase') }}</span>
            </div>

            @if (roomTypes().length === 0) {
              <ain-state type="empty" [title]="t('catalog.hospitality.rateCalendar.noRoomTypes')" />
            } @else {
              <div class="calendar-scroll">
                <div class="calendar-grid" [style.--day-count]="dayHeaders().length">
                  <div class="corner">{{ t('catalog.hospitality.roomType') }}</div>
                  @for (day of dayHeaders(); track day.key) {
                    <div class="day-head" [class.weekend]="day.isWeekend">
                      <strong>{{ day.label }}</strong>
                      <small>{{ day.subLabel }}</small>
                    </div>
                  }

                  @for (roomType of roomTypes(); track roomType.id) {
                    <div class="unit-label">
                      <strong>{{ roomType.typeCode }}</strong>
                      <small>{{ roomType.name }}</small>
                    </div>
                    <div class="unit-track">
                      <div class="day-grid">
                        @for (day of dayHeaders(); track day.key) {
                          <div
                            class="rate-cell"
                            [class.weekend]="day.isWeekend"
                            [class.override]="isOverride(roomType.id, day.date)"
                            [title]="rateTooltip(roomType, day.date)">
                            {{ resolveRate(roomType, day.date) | number:'1.0-0' }}
                          </div>
                        }
                      </div>
                    </div>
                  }
                </div>
              </div>
            }

            <section class="form-panel">
              <h4>{{ t('catalog.hospitality.rateCalendar.addEntry') }}</h4>
              <div class="form-grid">
                <label>
                  <span>{{ t('catalog.hospitality.roomType') }}</span>
                  <p-select [options]="roomTypeOptions()" [(ngModel)]="entryForm.roomTypeId" optionLabel="label" optionValue="value" appendTo="body" />
                </label>
                <label><span>{{ t('catalog.hospitality.rateCalendar.effectiveFrom') }}</span><ain-date-input [(ngModel)]="entryForm.effectiveFrom" /></label>
                <label><span>{{ t('catalog.hospitality.rateCalendar.effectiveTo') }}</span><ain-date-input [(ngModel)]="entryForm.effectiveTo" /></label>
                <label><span>{{ t('catalog.hospitality.nightlyRate') }}</span><p-inputNumber [(ngModel)]="entryForm.nightlyRate" [min]="0" /></label>
              </div>
              <button pButton type="button" [label]="t('catalog.hospitality.rateCalendar.saveEntry')" [disabled]="saving()" (click)="saveEntry()"></button>
            </section>

            <section class="entries-panel">
              <h4>{{ t('catalog.hospitality.rateCalendar.entries') }}</h4>
              <p-table [value]="entries()">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('catalog.hospitality.roomType') }}</th>
                    <th>{{ t('catalog.hospitality.rateCalendar.effectiveFrom') }}</th>
                    <th>{{ t('catalog.hospitality.rateCalendar.effectiveTo') }}</th>
                    <th>{{ t('catalog.hospitality.nightlyRate') }}</th>
                    <th></th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ roomTypeLabel(row.roomTypeId) }}</td>
                    <td>{{ row.effectiveFromUtc | date:'shortDate' }}</td>
                    <td>{{ row.effectiveToUtc | date:'shortDate' }}</td>
                    <td>{{ row.nightlyRate | number:'1.2-2' }}</td>
                    <td>
                      <button pButton type="button" size="small" severity="danger" [label]="t('catalog.hospitality.rateCalendar.removeEntry')" (click)="deleteEntry(row.id)"></button>
                    </td>
                  </tr>
                </ng-template>
              </p-table>
            </section>
          }
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .panel { display: grid; gap: 16px; }
    .toolbar { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
    .window-label { font-weight: 600; min-width: 140px; }
    .legend { display: flex; gap: 8px; }
    .chip { padding: 4px 10px; border-radius: 999px; font-size: 12px; }
    .chip.override { background: #dbeafe; color: #1d4ed8; }
    .chip.base { background: #f3f4f6; color: #374151; }
    .calendar-scroll { overflow-x: auto; border: 1px solid #e5e7eb; border-radius: 8px; }
    .calendar-grid {
      display: grid;
      grid-template-columns: 180px repeat(var(--day-count), minmax(72px, 1fr));
      min-width: max-content;
    }
    .corner, .day-head, .unit-label, .rate-cell {
      border-bottom: 1px solid #e5e7eb;
      border-right: 1px solid #e5e7eb;
      padding: 8px;
      font-size: 12px;
    }
    .corner, .day-head { background: #f9fafb; position: sticky; top: 0; z-index: 1; }
    .day-head { text-align: center; }
    .day-head.weekend, .rate-cell.weekend { background: #fafafa; }
    .unit-label { display: grid; gap: 2px; align-content: center; background: #fff; }
    .unit-track { padding: 0; }
    .day-grid { display: grid; grid-template-columns: repeat(var(--day-count), minmax(72px, 1fr)); }
    .rate-cell { text-align: center; font-weight: 600; }
    .rate-cell.override { background: #eff6ff; color: #1d4ed8; }
    .form-panel, .entries-panel { padding: 16px; border: 1px solid #e5e7eb; border-radius: 8px; display: grid; gap: 12px; }
    .form-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; }
    .form-grid label { display: grid; gap: 6px; }
    @media (max-width: 960px) { .form-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
    @media (max-width: 640px) { .form-grid { grid-template-columns: 1fr; } }
  `]
})
export class HospitalityRateCalendarComponent implements OnInit {
  private readonly service = inject(CatalogService);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly properties = signal<HospitalityProperty[]>([]);
  readonly ratePlans = signal<HospitalityRatePlan[]>([]);
  readonly roomTypes = signal<HospitalityRoomType[]>([]);
  readonly entries = signal<HospitalityRateCalendarEntry[]>([]);

  selectedPropertyId = '';
  selectedRatePlanId = '';
  windowStart = this.startOfDay(new Date());
  readonly windowDays = 21;

  entryForm = {
    roomTypeId: '',
    effectiveFrom: '',
    effectiveTo: '',
    nightlyRate: 0
  };

  readonly propertyOptions = computed(() =>
    this.properties().map(p => ({ label: `${p.propertyCode} — ${p.name}`, value: p.id }))
  );
  readonly ratePlanOptions = computed(() =>
    this.ratePlans().map(p => ({ label: `${p.planCode} — ${p.name}`, value: p.id }))
  );
  readonly roomTypeOptions = computed(() =>
    this.roomTypes().map(rt => ({ label: `${rt.typeCode} — ${rt.name}`, value: rt.id }))
  );

  readonly dayHeaders = computed<DayHeader[]>(() => {
    const headers: DayHeader[] = [];
    for (let i = 0; i < this.windowDays; i++) {
      const date = this.addDays(this.windowStart, i);
      headers.push({
        key: date.toISOString().slice(0, 10),
        label: date.toLocaleDateString(undefined, { weekday: 'short' }),
        subLabel: date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
        date,
        isWeekend: date.getDay() === 0 || date.getDay() === 6
      });
    }
    return headers;
  });

  readonly windowLabel = computed(() => {
    const end = this.addDays(this.windowStart, this.windowDays - 1);
    return `${this.windowStart.toLocaleDateString()} – ${end.toLocaleDateString()}`;
  });

  ngOnInit(): void {
    this.service.listHospitalityProperties().subscribe({
      next: rows => {
        this.properties.set(rows);
        this.loading.set(false);
      },
      error: () => {
        this.properties.set([]);
        this.loading.set(false);
      }
    });
  }

  onPropertyChange(): void {
    this.selectedRatePlanId = '';
    this.ratePlans.set([]);
    this.roomTypes.set([]);
    this.entries.set([]);
    if (!this.selectedPropertyId) return;

    this.service.listHospitalityRatePlans(this.selectedPropertyId).subscribe({
      next: rows => {
        this.ratePlans.set(rows);
        if (rows.length === 1) {
          this.selectedRatePlanId = rows[0].id;
          this.loadCalendar();
        }
      },
      error: () => this.ratePlans.set([])
    });

    this.service.listHospitalityRoomTypes(this.selectedPropertyId).subscribe({
      next: rows => {
        this.roomTypes.set(rows);
        if (rows.length && !this.entryForm.roomTypeId) {
          this.entryForm.roomTypeId = rows[0].id;
        }
      },
      error: () => this.roomTypes.set([])
    });
  }

  loadCalendar(): void {
    if (!this.selectedPropertyId || !this.selectedRatePlanId) {
      this.entries.set([]);
      return;
    }

    const from = this.windowStart.toISOString();
    const to = this.addDays(this.windowStart, this.windowDays).toISOString();
    this.service.listHospitalityRateCalendar({
      propertyId: this.selectedPropertyId,
      ratePlanId: this.selectedRatePlanId,
      fromUtc: from,
      toUtc: to
    }).subscribe({
      next: rows => this.entries.set(rows),
      error: () => this.entries.set([])
    });
  }

  shiftWindow(direction: -1 | 1): void {
    this.windowStart = this.addDays(this.windowStart, direction * this.windowDays);
    this.loadCalendar();
  }

  goToday(): void {
    this.windowStart = this.startOfDay(new Date());
    this.loadCalendar();
  }

  resolveRate(roomType: HospitalityRoomType, day: Date): number {
    const dayStart = this.startOfDay(day);
    const match = this.entries().find(entry =>
      entry.roomTypeId === roomType.id &&
      new Date(entry.effectiveFromUtc) <= dayStart &&
      new Date(entry.effectiveToUtc) > dayStart
    );
    return match?.nightlyRate ?? roomType.baseNightlyRate;
  }

  isOverride(roomTypeId: string, day: Date): boolean {
    const dayStart = this.startOfDay(day);
    return this.entries().some(entry =>
      entry.roomTypeId === roomTypeId &&
      new Date(entry.effectiveFromUtc) <= dayStart &&
      new Date(entry.effectiveToUtc) > dayStart
    );
  }

  rateTooltip(roomType: HospitalityRoomType, day: Date): string {
    const rate = this.resolveRate(roomType, day);
    const kind = this.isOverride(roomType.id, day)
      ? this.t('catalog.hospitality.rateCalendar.legendOverride')
      : this.t('catalog.hospitality.rateCalendar.legendBase');
    return `${kind}: ${rate}`;
  }

  roomTypeLabel(roomTypeId: string): string {
    const row = this.roomTypes().find(item => item.id === roomTypeId);
    return row ? `${row.typeCode} — ${row.name}` : roomTypeId;
  }

  saveEntry(): void {
    if (!this.selectedRatePlanId || !this.entryForm.roomTypeId || !this.entryForm.effectiveFrom || !this.entryForm.effectiveTo) return;
    if (this.entryForm.nightlyRate <= 0) return;

    const from = new Date(this.entryForm.effectiveFrom);
    const to = new Date(this.entryForm.effectiveTo);
    to.setDate(to.getDate() + 1);

    const payload: CreateHospitalityRateCalendarEntryPayload = {
      ratePlanId: this.selectedRatePlanId,
      roomTypeId: this.entryForm.roomTypeId,
      effectiveFromUtc: from.toISOString(),
      effectiveToUtc: to.toISOString(),
      nightlyRate: this.entryForm.nightlyRate
    };

    this.saving.set(true);
    this.service.createHospitalityRateCalendarEntry(payload).subscribe({
      next: () => {
        this.entryForm.nightlyRate = 0;
        this.saving.set(false);
        this.loadCalendar();
      },
      error: () => this.saving.set(false)
    });
  }

  deleteEntry(id: string): void {
    this.service.deleteHospitalityRateCalendarEntry(id).subscribe({
      next: () => this.loadCalendar(),
      error: () => undefined
    });
  }

  private startOfDay(value: Date): Date {
    const date = new Date(value);
    date.setHours(0, 0, 0, 0);
    return date;
  }

  private addDays(value: Date, days: number): Date {
    const date = new Date(value);
    date.setDate(date.getDate() + days);
    return date;
  }

  t(key: string): string { return this.localization.translate(key); }
}
